import { isDefined } from "@clipboard-health/util-ts";
import { MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES } from "@src/appV2/redesign/Shift/constants";
import { ExtraWorkedTimeType, type TimeRange } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, isEqual, parseISO } from "date-fns";

export function doesShiftHaveWorkedBreak(params: {
  mandatoryBreakDurationInMinutes: number;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
}): boolean {
  const { mandatoryBreakDurationInMinutes, clockInOut, lunchInOut } = params;

  const workDurationInMinutes =
    isDefined(clockInOut.start) && isDefined(clockInOut.end)
      ? differenceInMinutes(parseISO(clockInOut.end), parseISO(clockInOut.start))
      : 0;
  const breakDurationInMinutes =
    isDefined(lunchInOut?.start) && isDefined(lunchInOut?.end)
      ? differenceInMinutes(parseISO(lunchInOut.end), parseISO(lunchInOut.start))
      : 0;

  return (
    workDurationInMinutes > MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES &&
    breakDurationInMinutes < mandatoryBreakDurationInMinutes
  );
}

export interface AuthorizingEmployee {
  name: string;
  signature: string;
}

export function getExtraWorkedTime(props: {
  mandatoryBreakDurationInMinutes: number;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
  breakAuthorizationReason: string | undefined;
  authorizingEmployee?: AuthorizingEmployee;
}):
  | Array<{
      type: ExtraWorkedTimeType;
      authorization: { reason: string; authorizingEmployee?: AuthorizingEmployee };
    }>
  | undefined {
  const {
    mandatoryBreakDurationInMinutes,
    clockInOut,
    lunchInOut,
    breakAuthorizationReason,
    authorizingEmployee,
  } = props;

  const hasWorkedBreak =
    isDefined(breakAuthorizationReason) &&
    doesShiftHaveWorkedBreak({ mandatoryBreakDurationInMinutes, clockInOut, lunchInOut });

  if (!hasWorkedBreak) {
    return undefined;
  }

  const authorization = {
    reason: breakAuthorizationReason,
    ...(authorizingEmployee ? { authorizingEmployee } : {}),
  };
  return [{ type: ExtraWorkedTimeType.BREAK, authorization }];
}

export interface MissedPunch {
  type:
    | ExtraWorkedTimeType.CLOCK_IN
    | ExtraWorkedTimeType.CLOCK_OUT
    | ExtraWorkedTimeType.FULL_SHIFT
    | ExtraWorkedTimeType.BREAK;
  reason: string;
}

/**
 * Helper: Checks if submitted and actual times differ (including when actual time is missing)
 */
function timesDiffer(submittedTime: string | undefined, actualTime: string | undefined): boolean {
  // Missing actual time but submitted time exists = missed punch
  if (isDefined(submittedTime) && !isDefined(actualTime)) {
    return true;
  }

  // Both times exist but differ = missed punch
  if (isDefined(submittedTime) && isDefined(actualTime)) {
    return !isEqual(parseISO(submittedTime), parseISO(actualTime));
  }

  return false;
}

/**
 * Helper: Adds a missed punch entry to the array if the corresponding punch exists
 */
function addMissedPunchIfExists(
  extraWorkedTime: Array<{ type: ExtraWorkedTimeType; authorization: { reason: string } }>,
  missedPunches: MissedPunch[],
  type: ExtraWorkedTimeType
): void {
  const punch = missedPunches.find((p) => p.type === type);
  if (punch) {
    extraWorkedTime.push({
      type,
      authorization: { reason: punch.reason },
    });
  }
}

/**
 * Helper: Checks if break times differ between submitted and actual
 */
function breakTimesDiffer(
  submitLunchInOut: TimeRange | undefined,
  actualLunchInOut: TimeRange | undefined
): boolean {
  const breakStartDiffers = timesDiffer(
    submitLunchInOut?.start ?? undefined,
    actualLunchInOut?.start ?? undefined
  );
  const breakEndDiffers = timesDiffer(
    submitLunchInOut?.end ?? undefined,
    actualLunchInOut?.end ?? undefined
  );
  return breakStartDiffers || breakEndDiffers;
}

/**
 * Constructs extraWorkedTime entries for missed punch requests.
 *
 * For non-IP shifts: Pass FULL_SHIFT with reason (no validation needed)
 * For IP shifts: Pass CLOCK_IN, CLOCK_OUT, and/or BREAK with reasons, will validate against actual times
 *
 */
export function getMissedPunchExtraWorkedTime(props: {
  submitClockInOut: TimeRange;
  actualClockInOut: TimeRange | undefined;
  submitLunchInOut?: TimeRange;
  actualLunchInOut?: TimeRange;
  missedPunches: MissedPunch[] | undefined;
}): Array<{
  type: ExtraWorkedTimeType;
  authorization: { reason: string };
}> {
  const { submitClockInOut, actualClockInOut, submitLunchInOut, actualLunchInOut, missedPunches } =
    props;

  // If no missed punches provided, return empty array
  if (!isDefined(missedPunches) || missedPunches.length === 0) {
    return [];
  }

  // Non-IP: no actual clockInOut to compare against, return FULL_SHIFT as-is
  if (!isDefined(actualClockInOut)) {
    const fullShiftPunch = missedPunches.find((p) => p.type === ExtraWorkedTimeType.FULL_SHIFT);
    if (!fullShiftPunch) {
      return [];
    }

    return [
      { type: ExtraWorkedTimeType.FULL_SHIFT, authorization: { reason: fullShiftPunch.reason } },
    ];
  }

  // IP: validate missed punches against actual time differences
  const extraWorkedTime: Array<{
    type: ExtraWorkedTimeType;
    authorization: { reason: string };
  }> = [];

  // Check clock-in time difference
  if (timesDiffer(submitClockInOut.start ?? undefined, actualClockInOut.start ?? undefined)) {
    addMissedPunchIfExists(extraWorkedTime, missedPunches, ExtraWorkedTimeType.CLOCK_IN);
  }

  // Check clock-out time difference
  if (timesDiffer(submitClockInOut.end ?? undefined, actualClockInOut.end ?? undefined)) {
    addMissedPunchIfExists(extraWorkedTime, missedPunches, ExtraWorkedTimeType.CLOCK_OUT);
  }

  // Check break time differences
  if (breakTimesDiffer(submitLunchInOut, actualLunchInOut)) {
    addMissedPunchIfExists(extraWorkedTime, missedPunches, ExtraWorkedTimeType.BREAK);
  }

  return extraWorkedTime;
}
